// src/components/VictorDebrief.jsx
// Post-deal debrief flow triggered when an opportunity moves to Closed Won or Lost.
// Feeds the Outcome Ledger which powers the monthly KB distillation.

import React, { useState } from 'react';
import './VictorDebrief.css';
import { apiFetch } from '../lib/api';

// ── Step definitions ────────────────────────────────────────────────────────
const STEPS = [
  { id: 'outcome',    label: 'The Result',     icon: '🏁' },
  { id: 'deal',       label: 'Deal Profile',   icon: '🏢' },
  { id: 'meddic',     label: 'MEDDIC Review',  icon: '📊' },
  { id: 'strategy',   label: 'Strategy',       icon: '🎯' },
  { id: 'ai_review',  label: 'AI Performance', icon: '🤖' },
  { id: 'reflect',    label: 'Reflection',     icon: '💡' },
];

const EMPTY_FORM = {
  outcome: '',
  closeDate: new Date().toISOString().split('T')[0],
  dealSize: '',
  carrierSize: '',
  competitor: '',
  entryMotion: '',
  championStrength: '',
  economicBuyerEngaged: '',
  meddicScoreAtClose: '',
  painClarity: '',
  decidingFactor: '',
  whatWeWouldDoDifferently: '',
  victorCoachingUsed: '',
  victorPredictionAccuracy: '',
  victorCoachingGap: '',
  selmaSignalQuality: '',
  cyranoOutreachEffectiveness: '',
  notes: '',
};

// ── Option sets ─────────────────────────────────────────────────────────────
const OUTCOMES = [
  { value: 'Won',         label: '🏆 Won',         color: 'var(--dm-green)', bg: 'var(--dm-green-light)' },
  { value: 'Lost',        label: '❌ Lost',         color: 'var(--dm-red)', bg: 'var(--dm-red-light)' },
  { value: 'Tabled',      label: '⏸️ Tabled',      color: 'var(--dm-amber)', bg: 'var(--dm-amber-light)' },
  { value: 'No Decision', label: '🔘 No Decision', color: 'var(--dm-grey-600)', bg: '#F3F4F6' },
];

const DEAL_SIZES = ['$100K-$300K','$300K-$600K','$500K-$1.2M','$600K-$1M','$800K-$1.5M','$1M+'];
const CARRIER_SIZES = ['$50M-$300M','$300M-$1B','$1B-$3B','$3B-$5B','$5B+'];
const COMPETITORS = ['Deloitte','Accenture','EY','KPMG','PwC','Regional Boutique','Internal IT','Duck Creek SI','None/Direct','Unknown'];
const ENTRY_MOTIONS = ['Readiness Assessment','Stabilization/Rescue','Cold RFP','Warm Referral','Partner Introduction','Conference/Event','LinkedIn Outreach'];

const CHAMPION_OPTIONS = [
  { value: 'Strong — introduced to EB',    label: '💪 Strong — introduced to EB',    color: 'var(--dm-green)' },
  { value: 'Moderate — helpful but limited', label: '🤝 Moderate — helpful but limited', color: 'var(--dm-amber)' },
  { value: 'Weak — coach only',            label: '👋 Weak — coach only',            color: '#F97316' },
  { value: 'None',                         label: '❌ None',                         color: 'var(--dm-red)' },
];

const EB_OPTIONS = [
  { value: 'Yes — direct relationship', label: '✅ Yes — direct relationship', color: 'var(--dm-green)' },
  { value: 'Partial — through champion', label: '⚡ Partial — through champion', color: 'var(--dm-amber)' },
  { value: 'No — never reached',        label: '❌ No — never reached',        color: 'var(--dm-red)' },
];

const MEDDIC_OPTIONS = [
  { value: '25-30 Strong',       label: '🟢 25–30 Strong',       color: 'var(--dm-green)' },
  { value: '18-24 Developing',   label: '🟡 18–24 Developing',   color: 'var(--dm-amber)' },
  { value: '12-17 Weak',         label: '🟠 12–17 Weak',         color: '#F97316' },
  { value: 'Below 12 Unqualified', label: '🔴 Below 12 Unqualified', color: 'var(--dm-red)' },
];

const PAIN_OPTIONS = [
  { value: 'Real pain confirmed', label: '✅ Real pain confirmed',  color: 'var(--dm-green)' },
  { value: 'Symptom level only',  label: '⚠️ Symptom level only',  color: 'var(--dm-amber)' },
  { value: 'Unknown',             label: '❓ Unknown',              color: 'var(--dm-grey-600)' },
];

const AI_USED_OPTIONS = [
  { value: 'Yes — heavily',   label: '🔥 Yes — heavily',   color: 'var(--dm-green)' },
  { value: 'Yes — partially', label: '⚡ Yes — partially', color: 'var(--dm-amber)' },
  { value: 'No',              label: '—  No',              color: 'var(--dm-grey-600)' },
];

const AI_ACCURACY_OPTIONS = [
  { value: 'Accurate',            label: '✅ Accurate',             color: 'var(--dm-green)' },
  { value: 'Partially accurate',  label: '⚠️ Partially accurate',  color: 'var(--dm-amber)' },
  { value: 'Off — document why',  label: '❌ Off — document why',   color: 'var(--dm-red)' },
];

const SIGNAL_QUALITY_OPTIONS = [
  { value: 'High — signals were fresh and relevant', label: '🔥 High — fresh & relevant',      color: 'var(--dm-green)' },
  { value: 'Medium — some useful signals',           label: '⚡ Medium — some useful signals', color: 'var(--dm-amber)' },
  { value: 'Low — signals were stale or irrelevant', label: '💧 Low — stale or irrelevant',    color: 'var(--dm-red)' },
];

const CYRANO_OPTIONS = [
  { value: 'High — strong reply rate',   label: '🔥 High — strong reply rate',  color: 'var(--dm-green)' },
  { value: 'Medium — some engagement',   label: '⚡ Medium — some engagement',  color: 'var(--dm-amber)' },
  { value: 'Low — minimal response',     label: '💧 Low — minimal response',    color: 'var(--dm-red)' },
  { value: 'Not used',                   label: '—  Not used',                  color: 'var(--dm-grey-600)' },
];

// ── Sub-components ───────────────────────────────────────────────────────────

function OptionPills({ options, value, onChange }) {
  return (
    <div className="vd-pills">
      {options.map(opt => (
        <button
          key={opt.value || opt}
          className={`vd-pill ${value === (opt.value || opt) ? 'vd-pill--active' : ''}`}
          style={value === (opt.value || opt) ? {
            background: (opt.bg || opt.color + '20'),
            borderColor: opt.color || 'var(--dm-indigo)',
            color: opt.color || 'var(--dm-indigo)',
          } : {}}
          onClick={() => onChange(opt.value || opt)}
          type="button"
        >
          {opt.label || opt}
        </button>
      ))}
    </div>
  );
}

function TextArea({ label, hint, value, onChange, placeholder, required }) {
  return (
    <div className="vd-field">
      <label className="vd-label">
        {label}
        {required && <span className="vd-required">*</span>}
      </label>
      {hint && <p className="vd-hint">{hint}</p>}
      <textarea
        className="vd-textarea"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
      />
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export default function VictorDebrief({
  opportunityName,
  accountName,
  opportunityNotionUrl,
  onClose,
  onComplete,
}) {
  const [step, setStep]       = useState(0);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState(null);

  const set = (field) => (value) => setForm(prev => ({ ...prev, [field]: value }));

  const canAdvance = () => {
    if (step === 0) return !!form.outcome;
    if (step === 1) return !!form.dealSize && !!form.carrierSize && !!form.entryMotion;
    if (step === 2) return !!form.championStrength && !!form.economicBuyerEngaged && !!form.meddicScoreAtClose;
    if (step === 3) return !!form.decidingFactor;
    if (step === 4) return !!form.victorCoachingUsed;
    return true;
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch('/api/victor/debrief', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          opportunityName,
          accountName,
          opportunityNotionUrl,
          ...form,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSaved(true);
        onComplete && onComplete(data);
      } else {
        setError(data.error || 'Save failed');
      }
    } catch (err) {
      setError('Network error — please try again');
    } finally {
      setSaving(false);
    }
  };

  if (saved) {
    return (
      <div className="vd-overlay">
        <div className="vd-modal vd-modal--success">
          <div className="vd-success-icon">🧠</div>
          <h2 className="vd-success-title">Debrief Captured</h2>
          <p className="vd-success-body">
            Victor has logged this outcome. When enough deals accumulate, the monthly
            distillation will extract patterns and sharpen his coaching —
            along with Selma's signal scoring, Cyrano's outreach, and Jarvis's planning.
          </p>
          <div className="vd-success-stat">
            <span>Outcome saved to</span>
            <strong>Outcome Ledger</strong>
          </div>
          <button className="vd-btn vd-btn--primary" onClick={onClose}>Done</button>
        </div>
      </div>
    );
  }



  return (
    <div className="vd-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="vd-modal">

        {/* ── Header ── */}
        <div className="vd-header">
          <div className="vd-header-left">
            <div className="vd-victor-badge">
              <span className="vd-victor-icon">⚡</span>
              <span className="vd-victor-label">Victor</span>
            </div>
            <div>
              <h2 className="vd-title">Deal Debrief</h2>
              <p className="vd-subtitle">{opportunityName} · {accountName}</p>
            </div>
          </div>
          <button className="vd-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* ── Step nav ── */}
        <div className="vd-stepper">
          {STEPS.map((s, i) => (
            <div
              key={s.id}
              className={`vd-step ${i === step ? 'vd-step--active' : ''} ${i < step ? 'vd-step--done' : ''}`}
              onClick={() => i < step && setStep(i)}
            >
              <div className="vd-step-dot">
                {i < step ? '✓' : s.icon}
              </div>
              <span className="vd-step-label">{s.label}</span>
            </div>
          ))}
        </div>

        {/* ── Progress bar ── */}
        <div className="vd-progress-track">
          <div
            className="vd-progress-fill"
            style={{ width: `${((step) / (STEPS.length - 1)) * 100}%` }}
          />
        </div>

        {/* ── Step content ── */}
        <div className="vd-body">

          {/* STEP 0 — Outcome */}
          {step === 0 && (
            <div className="vd-section">
              <h3 className="vd-section-title">How did it end?</h3>
              <p className="vd-section-hint">
                Every outcome — win or loss — teaches the system something. No spin needed.
              </p>
              <div className="vd-outcome-grid">
                {OUTCOMES.map(opt => (
                  <button
                    key={opt.value}
                    className={`vd-outcome-card ${form.outcome === opt.value ? 'vd-outcome-card--active' : ''}`}
                    style={form.outcome === opt.value ? {
                      background: opt.bg,
                      borderColor: opt.color,
                    } : {}}
                    onClick={() => set('outcome')(opt.value)}
                    type="button"
                  >
                    <span className="vd-outcome-label" style={form.outcome === opt.value ? { color: opt.color } : {}}>
                      {opt.label}
                    </span>
                  </button>
                ))}
              </div>
              <div className="vd-field vd-field--inline">
                <label className="vd-label">Close Date</label>
                <input
                  type="date"
                  className="vd-input"
                  value={form.closeDate}
                  onChange={e => set('closeDate')(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* STEP 1 — Deal Profile */}
          {step === 1 && (
            <div className="vd-section">
              <h3 className="vd-section-title">Deal Profile</h3>

              <div className="vd-field">
                <label className="vd-label">Deal Size <span className="vd-required">*</span></label>
                <OptionPills options={DEAL_SIZES} value={form.dealSize} onChange={set('dealSize')} />
              </div>

              <div className="vd-field">
                <label className="vd-label">Carrier Size (DWP) <span className="vd-required">*</span></label>
                <OptionPills options={CARRIER_SIZES} value={form.carrierSize} onChange={set('carrierSize')} />
              </div>

              <div className="vd-field">
                <label className="vd-label">How we got in <span className="vd-required">*</span></label>
                <OptionPills options={ENTRY_MOTIONS} value={form.entryMotion} onChange={set('entryMotion')} />
              </div>

              <div className="vd-field">
                <label className="vd-label">Primary Competitor</label>
                <OptionPills options={COMPETITORS} value={form.competitor} onChange={set('competitor')} />
              </div>
            </div>
          )}

          {/* STEP 2 — MEDDIC */}
          {step === 2 && (
            <div className="vd-section">
              <h3 className="vd-section-title">MEDDIC at Close</h3>
              <p className="vd-section-hint">
                Honest assessment of where qualification stood when the deal ended.
              </p>

              <div className="vd-field">
                <label className="vd-label">Champion Strength <span className="vd-required">*</span></label>
                <OptionPills options={CHAMPION_OPTIONS} value={form.championStrength} onChange={set('championStrength')} />
              </div>

              <div className="vd-field">
                <label className="vd-label">Economic Buyer Engaged <span className="vd-required">*</span></label>
                <OptionPills options={EB_OPTIONS} value={form.economicBuyerEngaged} onChange={set('economicBuyerEngaged')} />
              </div>

              <div className="vd-field">
                <label className="vd-label">Overall MEDDIC Score <span className="vd-required">*</span></label>
                <OptionPills options={MEDDIC_OPTIONS} value={form.meddicScoreAtClose} onChange={set('meddicScoreAtClose')} />
              </div>

              <div className="vd-field">
                <label className="vd-label">Pain Clarity</label>
                <OptionPills options={PAIN_OPTIONS} value={form.painClarity} onChange={set('painClarity')} />
              </div>
            </div>
          )}

          {/* STEP 3 — Strategy */}
          {step === 3 && (
            <div className="vd-section">
              <h3 className="vd-section-title">What actually decided it?</h3>
              <p className="vd-section-hint">
                Be specific. "They chose Deloitte because of brand" is less useful than
                "CFO had a prior Deloitte relationship and procurement never gave us access to him."
              </p>

              <TextArea
                label="Deciding Factor"
                hint="The one thing that most determined this outcome."
                value={form.decidingFactor}
                onChange={set('decidingFactor')}
                placeholder="e.g. Champion was a VP of IT who couldn't get CFO access. Deal died in procurement when large SI offered bundled pricing."
                required
              />

              <TextArea
                label="What We'd Do Differently"
                hint="One or two specific strategy changes in hindsight."
                value={form.whatWeWouldDoDifferently}
                onChange={set('whatWeWouldDoDifferently')}
                placeholder="e.g. We should have pushed for a CFO conversation by meeting 3, not meeting 6. The assessment scope was too narrow to demonstrate integration depth."
              />
            </div>
          )}

          {/* STEP 4 — AI Performance */}
          {step === 4 && (
            <div className="vd-section">
              <h3 className="vd-section-title">How did the AI team perform?</h3>
              <p className="vd-section-hint">
                This is the most important step for making the system smarter.
                Honest feedback — including what the AIs got wrong — is what drives improvement.
              </p>

              <div className="vd-field">
                <label className="vd-label">Victor Coaching Used <span className="vd-required">*</span></label>
                <OptionPills options={AI_USED_OPTIONS} value={form.victorCoachingUsed} onChange={set('victorCoachingUsed')} />
              </div>

              {form.victorCoachingUsed !== 'No' && (
                <>
                  <div className="vd-field">
                    <label className="vd-label">Victor Prediction Accuracy</label>
                    <OptionPills options={AI_ACCURACY_OPTIONS} value={form.victorPredictionAccuracy} onChange={set('victorPredictionAccuracy')} />
                  </div>

                  {form.victorPredictionAccuracy === 'Off — document why' && (
                    <TextArea
                      label="What Victor Got Wrong"
                      hint="This goes directly into the next knowledge base update."
                      value={form.victorCoachingGap}
                      onChange={set('victorCoachingGap')}
                      placeholder="e.g. Victor kept scoring Champion as Moderate but the contact was actually a coach not a champion — we needed to identify that gap earlier."
                    />
                  )}
                </>
              )}

              <div className="vd-field">
                <label className="vd-label">Selma Signal Quality</label>
                <OptionPills options={SIGNAL_QUALITY_OPTIONS} value={form.selmaSignalQuality} onChange={set('selmaSignalQuality')} />
              </div>

              <div className="vd-field">
                <label className="vd-label">Cyrano Outreach Effectiveness</label>
                <OptionPills options={CYRANO_OPTIONS} value={form.cyranoOutreachEffectiveness} onChange={set('cyranoOutreachEffectiveness')} />
              </div>
            </div>
          )}

          {/* STEP 5 — Reflection */}
          {step === 5 && (
            <div className="vd-section">
              <h3 className="vd-section-title">Anything else worth capturing?</h3>
              <p className="vd-section-hint">
                Context that doesn't fit the structured fields — internal politics,
                timing factors, relationship history, or things that surprised you.
              </p>

              <TextArea
                label="Additional Notes"
                value={form.notes}
                onChange={set('notes')}
                placeholder="Optional — anything the structured fields didn't capture."
              />

              {/* Summary preview */}
              <div className="vd-summary">
                <div className="vd-summary-title">Debrief Summary</div>
                <div className="vd-summary-grid">
                  <div className="vd-summary-item">
                    <span className="vd-summary-label">Result</span>
                    <span className="vd-summary-value">{form.outcome || '—'}</span>
                  </div>
                  <div className="vd-summary-item">
                    <span className="vd-summary-label">Entry Motion</span>
                    <span className="vd-summary-value">{form.entryMotion || '—'}</span>
                  </div>
                  <div className="vd-summary-item">
                    <span className="vd-summary-label">Champion</span>
                    <span className="vd-summary-value">{form.championStrength?.split(' — ')[0] || '—'}</span>
                  </div>
                  <div className="vd-summary-item">
                    <span className="vd-summary-label">EB Access</span>
                    <span className="vd-summary-value">{form.economicBuyerEngaged?.split(' — ')[0] || '—'}</span>
                  </div>
                  <div className="vd-summary-item">
                    <span className="vd-summary-label">MEDDIC</span>
                    <span className="vd-summary-value">{form.meddicScoreAtClose?.split(' ')[0] || '—'}</span>
                  </div>
                  <div className="vd-summary-item">
                    <span className="vd-summary-label">Victor Used</span>
                    <span className="vd-summary-value">{form.victorCoachingUsed?.split(' — ')[0] || '—'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="vd-error">⚠️ {error}</div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="vd-footer">
          {step > 0 && (
            <button
              className="vd-btn vd-btn--ghost"
              onClick={() => setStep(s => s - 1)}
              type="button"
            >
              ← Back
            </button>
          )}
          <div className="vd-footer-right">
            <span className="vd-step-count">{step + 1} of {STEPS.length}</span>
            {step < STEPS.length - 1 ? (
              <button
                className="vd-btn vd-btn--primary"
                onClick={() => setStep(s => s + 1)}
                disabled={!canAdvance()}
                type="button"
              >
                Next →
              </button>
            ) : (
              <button
                className="vd-btn vd-btn--submit"
                onClick={handleSubmit}
                disabled={saving}
                type="button"
              >
                {saving ? (
                  <><span className="vd-spinner" /> Saving…</>
                ) : (
                  '🧠 Save Debrief'
                )}
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
